import React, { useCallback, useEffect, useMemo, useState, memo } from 'react'
import { dequal as deepEqual } from 'dequal'

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getSortedRowModel,
  functionalUpdate,
} from '@tanstack/react-table'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa'
import IndeterminateCheckbox from '../components/IndeterminateCheckbox'

import { Box, Flex, Select, Skeleton, Table, Tag, Tbody, Td, Text, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import { TooltipCellRenderer2 } from '../components/Cell/ToolTipCell/TooltipCell'

import { camelToWords } from '@/utils/stringHelper'
import DoubleScrollbar from '@/components/Elements/Scrollbar/DoubleScrollbar'
import TablePagination from '../components/TablePagination'
import TableToolbar from '../components/TableToolbar'
import TableFilter from '../components/Filter/TableFilter'
import { isNullish } from '@/utils/helper'
import { TextCell } from '../components/Cell'

const defaultColumn = {
  cell: TextCell,
  size: 200,
  maxSize: 1200,

  header: ({ header: { id } }) => {
    if (isNullish(id)) {
      return <span>(-)</span>
    }
    return <span>{camelToWords(id)}</span>
  },
}

export const tableInitialState = {
  columnSizing: {},
  columnSizingInfo: {
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    isResizingColumn: false,
    columnSizingStart: [],
  },
  rowSelection: {},
  rowPinning: {
    top: [],
    bottom: [],
  },
  expanded: {},
  grouping: [],
  sorting: [],
  columnFilters: [],
  columnPinning: {
    left: [],
    right: [],
  },
  columnOrder: [],
  columnVisibility: {},
  pagination: {
    pageIndex: 0,
    pageSize: 10,
  },
}

const TableBody = ({ table, handleRowClick }) => {
  return (
    <Tbody>
      {table.getRowModel().rows.map((row) => {
        return (
          <Tr key={row.id} onClick={() => handleRowClick(row)}>
            {row.getVisibleCells().map((cell) => (
              <Td
                key={cell.id}
                p={2}
                textAlign={'left'}
                h="50px"
                overflow="hidden"
                textOverflow={'ellipsis'}
                whiteSpace={'nowrap'}
              >
                <Box w="100%" maxW="400px">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              </Td>
            ))}
          </Tr>
        )
      })}
    </Tbody>
  )
}

const MemoizedTableBody = memo(TableBody, (prev, next) => prev.table.options.data === next.table.options.data)

const FullTable = ({
  columnDef,
  data,
  rowCount,
  state,
  setState,
  handleRowClick = () => {},
  enableRowSelection,
  isLoading = false,
  showColumnGroup = false,
  displayLevel = '',
}) => {
  const tableInstance = useReactTable({
    data,
    rowCount: rowCount,
    columns: columnDef,
    defaultColumn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    //Select
    enableRowSelection: enableRowSelection,

    //Pagination
    autoResetPageIndex: false,

    manualPagination: true,
    //Resize
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',

    //Filter
    manualFiltering: true,
    manualSorting: true,

    //Debug
    // debugTable: true,
    // debugHeaders: true,
  })

  tableInstance.setOptions((prev) => ({
    ...prev,
    state: state,

    onStateChange: (updater) => {
      const newState = functionalUpdate(updater, state)

      if (
        newState?.columnFilters &&
        state?.columnFilters &&
        !deepEqual(newState?.columnFilters, state?.columnFilters)
      ) {
        setState({
          ...newState,
          pagination: {
            pageIndex: 0,
            pageSize: newState?.pagination?.pageSize ?? 10,
          },
        })
      } else {
        setState(newState)
      }

      // setState(newState)
    },
    // onStateChange: setState,
  }))

  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const columnSizeVars = React.useMemo(() => {
    const headers = tableInstance.getFlatHeaders()
    const colSizes = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]
      colSizes[`--header-${header.id}-size`] = header.getSize()
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize()
    }
    return colSizes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInstance.getState().columnSizingInfo, tableInstance.getState().columnSizing])

  const showMemorizedTable = tableInstance.getState().columnSizingInfo.isResizingColumn
  const showLoading = !showMemorizedTable && isLoading
  const showNormalTable = !showMemorizedTable && !isLoading

  const showSelectionHeader = typeof enableRowSelection === 'function'
  const hasSelectableRowsOnPage = tableInstance.getRowModel().rows.some((row) => row.getCanSelect())
  const isAllPageRowsSelected = tableInstance.getIsAllPageRowsSelected?.() ?? false
  const isSomePageRowsSelected = tableInstance.getIsSomePageRowsSelected?.() ?? false

  return (
    <Box>
      <TableToolbar table={tableInstance} displayLevel={displayLevel}></TableToolbar>

      <Flex mt="3">
        <TablePagination table={tableInstance} dataCount={tableInstance.getPageCount()} />
      </Flex>
      <DoubleScrollbar>
        <Box minH="500px">
          <Table
            colorScheme="gray"
            variant="striped"
            border="1px solid rgb(226, 232, 240)"
            borderRadius="md"
            w={tableInstance.getCenterTotalSize()}
            minW={'100%'}
          >
            <Thead>
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  {showColumnGroup && (
                    <Tr key={`${headerGroup.id}-group`} bg="white">
                      {headerGroup.headers.map((header) => (
                        <Th
                          key={`${header.id}-group`}
                          p={2}
                          textAlign={'center'}
                          colSpan={header.colSpan}
                          w={header.getSize()}
                          h={'50px'}
                          position={'relative'}
                          role={'group'}
                        >
                          {header.column.columnDef?.meta?.displayLevel ? (
                            <Tag colorScheme={header.column.columnDef?.meta?.displayColor ?? 'white'} variant="solid">
                              {header.column.columnDef?.meta?.displayLevel}
                            </Tag>
                          ) : (
                            <span></span>
                          )}
                        </Th>
                      ))}
                    </Tr>
                  )}
                  <Tr key={`header-${headerGroup.id}`}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        p={2}
                        textAlign={'left'}
                        colSpan={header.colSpan}
                        w={header.getSize()}
                        h={'50px'}
                        position={'relative'}
                        role={'group'}
                      >
                        <Flex h="100%" direction="column" justifyContent="space-between">
                          {showSelectionHeader && header.id === 'select' ? (
                            <Flex alignItems="center" gap={2} w="100%">
                              <IndeterminateCheckbox
                                checked={isAllPageRowsSelected}
                                indeterminate={isSomePageRowsSelected}
                                disabled={!hasSelectableRowsOnPage}
                                onChange={tableInstance.getToggleAllPageRowsSelectedHandler?.()}
                              />
                              <Box>{flexRender(header.column.columnDef.header, header.getContext())}</Box>
                            </Flex>
                          ) : (
                            <Flex
                              _hover={{ cursor: 'pointer' }}
                              w="100%"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <Box w="100%"> {flexRender(header.column.columnDef.header, header.getContext())}</Box>

                              {/* Sorting */}
                              {{ asc: <FaAngleUp />, desc: <FaAngleDown /> }[`${header.column.getIsSorted()}`] ?? null}
                            </Flex>
                          )}

                          {/* Filter */}
                          {header.column.getCanFilter() ? (
                            <Box mt="1">
                              <TableFilter
                                column={header.column}
                                updatePagination={() => tableInstance.setPageIndex(0)}
                              />
                            </Box>
                          ) : null}
                        </Flex>

                        {/* Resize Handler */}
                        <Box
                          position={'absolute'}
                          top={0}
                          right={0}
                          w="7px"
                          height={'100%'}
                          bg={header.column.getIsResizing() ? 'gray.400' : ''}
                          _groupHover={{
                            bg: header.column.getIsResizing() ? 'gray.400' : 'teal.400',
                            cursor: 'col-resize',
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation()
                            header.getResizeHandler()(e)
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation()
                            header.getResizeHandler()(e)
                          }}
                          onDoubleClick={() => header.column.resetSize()}
                        />
                      </Th>
                    ))}
                  </Tr>
                </React.Fragment>
              ))}
            </Thead>

            {showLoading && (
              <Tbody>
                {Array.from({ length: 10 }).map((_, i) => (
                  <Tr key={i} bg="white">
                    <Td colSpan={tableInstance.getAllColumns().length} textAlign="center" bg="white">
                      <Skeleton height="20px" />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}

            {showMemorizedTable && <MemoizedTableBody table={tableInstance} handleRowClick={handleRowClick} />}

            {showNormalTable && <TableBody table={tableInstance} handleRowClick={handleRowClick} />}

            {tableInstance.getFooterProps && (
              <Tfoot>
                <Tr>
                  {tableInstance.getFooterProps().map((footer) => (
                    <Td key={footer.id} p={2} textAlign={'left'} w="300px">
                      {flexRender(footer.column.columnDef.footer, footer.getContext())}
                    </Td>
                  ))}
                </Tr>
              </Tfoot>
            )}
          </Table>
        </Box>
      </DoubleScrollbar>
      <Flex mt="3">
        <TablePagination table={tableInstance} dataCount={tableInstance.getPageCount()} />
      </Flex>
    </Box>
  )
}

export default FullTable
